import { MenuItem, useDisclosure } from "@chakra-ui/react";
import {
  CommitmentClassFormFields,
  useCommitmentClassEditForm,
} from "features/commitment-classes/setup/CommitmentClassForm";
import { CommitmentClassTabProps } from "features/commitment-classes/setup/CommitmentClassTabProps";
import { useInvestorClassRetrieveSuspense } from "gen/endpoints/investor-class/investor-class";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { PageTabBody } from "shared/components/layout/PageTabBody";

export const CommitmentClassTabBasicDetails: React.FC = () => {
  const { t } = useTranslation(["commitmentClass", "common"]);

  const { fundFamilyId, investorClassId } =
    useOutletContext<CommitmentClassTabProps>();

  const { data: investorClassData, refetch } = useInvestorClassRetrieveSuspense(
    fundFamilyId,
    investorClassId,
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const onCloseComplete = () => {
    onClose();
    void refetch();
  };

  return (
    <PageTabBody>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockHeader>
            <Restricted permission={PermissionTypeEnum.config}>
              <ContentBlockActions>
                <ContentBlockMenu>
                  <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
                </ContentBlockMenu>
              </ContentBlockActions>
            </Restricted>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("commitmentClass:createModal.name")}>
                {investorClassData.name}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:notesOptional")}>
                {investorClassData.notes}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      {isOpen && (
        <CommitmentClassEditDrawer
          fundFamilyId={fundFamilyId}
          investorClassId={investorClassId}
          onCloseComplete={onCloseComplete}
        />
      )}
    </PageTabBody>
  );
};

const CommitmentClassEditDrawer: React.FC<{
  fundFamilyId: string;
  investorClassId: string;
  onCloseComplete: () => void;
}> = ({ fundFamilyId, investorClassId, onCloseComplete }) => {
  const { t } = useTranslation(["commitmentClass", "common"]);
  const formProps = useCommitmentClassEditForm(fundFamilyId, investorClassId);
  return (
    <FormDrawer
      title={t("commitmentClass:edit.title")}
      formProps={formProps}
      onCloseComplete={onCloseComplete}
    >
      <CommitmentClassFormFields />
    </FormDrawer>
  );
};
